import { AnyObject, Maybe } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { environmentConfig } from '../../configs/EnvironmentConfig';
import { liveApplicationConfig } from '../../configs/LiveApplicationConfig';
import { liveTrackingConfig } from '../../configs/LiveTrackingConfig';
import { ELiveErrorCode } from '../../types/error';
import { getErrorCode, getErrorName, LiveError } from '../../utils/error';
import { getCircularReplacer } from '../../utils/general/json';
import {
  ELiveErrorCategory,
  getLiveApplicationInfo,
  ILiveErrorAdditionalInfo,
} from './datadog_data';

/**
 * Logger for Datadog tracking.
 */
const log: Logger = new Logger('🦩Datadog', liveApplicationConfig.TRACKING.IS_DATADOG_LOG_ENABLED);

/**
 * Check whether error is one of agora permission errors.
 */
export function isAgoraIgnoredError<T extends AnyObject>(error?: Maybe<T>): boolean {
  return (
    getErrorName(error) === 'AgoraRTCException' &&
    liveApplicationConfig.TRACKING.NR_AGORA_IGNORED_CODES.includes(getErrorCode(error))
  );
}

/**
 * Check whether error/action can be tracked to Datadog.
 *  - Check if error code is OK to be tracked and not ignored
 *  - Check if user ID of current context is not ignored
 */
export function isAllowedToTrack<T extends AnyObject>(error?: Maybe<T> | Error | string): boolean {
  return (
    !liveApplicationConfig.TRACKING.NR_IGNORED_USERS.includes(
      liveTrackingConfig.DETAILS.USER_ID as number,
    ) && !isAgoraIgnoredError(error as Maybe<T>)
  );
}

/**
 * Track live exception for Datadog investigations.
 */
export function trackLiveError(
  error: Error | string,
  additionalInfo?: ILiveErrorAdditionalInfo,
): void {
  /**
   * Track only errors that are not problematic.
   */
  if (!isAllowedToTrack(error)) {
    return;
  }

  const errorInfo: AnyObject = Object.assign({}, additionalInfo, getLiveApplicationInfo());

  /**
   * Convert error info to string and correctly handle circular dependencies.
   */
  if (additionalInfo?.data) {
    errorInfo.data = JSON.stringify(additionalInfo.data, getCircularReplacer());
  }

  /**
   * Get additional live error info and correctly handle circular dependencies.
   */
  if (error instanceof LiveError) {
    errorInfo.liveErrorDetails = JSON.stringify(error.data, getCircularReplacer());
    errorInfo.liveErrorCode = ELiveErrorCode[error.code] || ELiveErrorCode.GENERIC;
  }

  /**
   * Respect event-level based limit of tracked errors.
   * If everything breaks after some period of time, do not cause spikes based on 1 page.
   */
  if (
    liveApplicationConfig.TRACKING.NR_LOG_ERRORS_TRACKED <=
    liveApplicationConfig.TRACKING.NR_LOG_ERRORS_LIMIT
  ) {
    liveApplicationConfig.TRACKING.NR_LOG_ERRORS_TRACKED += 1;
  } else {
    return log.warn('Skipping error tracking, session facing limit');
  }

  /**
   * Skip any attempts from other environments:
   *  - Skip SSR reports
   *  - Skip DEV/CI reports
   *  - Skip test env with jest etc
   */
  if (environmentConfig.IS_COM && window.DD_RUM) {
    window.DD_RUM.onReady(() => {
      const globals: Maybe<AnyObject> = window.DD_RUM.getUser() ?? null;
      log.info('Sending Datadog report:', error, errorInfo, globals);
      window.DD_RUM.addError(error, errorInfo);
    });
  } else {
    log.info('Got local Datadog report:', error, errorInfo);
  }
}

/**
 * Track live interaction with timing and corresponding info.
 */
export function trackLiveAction(action: string, info: AnyObject = {}): void {
  /**
   * Track only errors that are not problematic.
   */
  if (!isAllowedToTrack()) {
    return;
  }

  const actionInfo: AnyObject = Object.assign({}, info, getLiveApplicationInfo());

  Logger.mark(action);

  /**
   * Skip any attempts from other environments:
   *  - Skip SSR reports
   *  - Skip DEV/CI reports
   *  - Skip test env with jest etc
   */
  if (environmentConfig.IS_COM && window.DD_RUM) {
    window.DD_RUM.onReady(() => {
      const globals: Maybe<AnyObject> = window.DD_RUM.getUser() ?? null;
      log.info('Sending Datadog action:', action, actionInfo, globals);
      window.DD_RUM.addAction(action, actionInfo);
    });
  } else {
    log.info('Got local Datadog action:', action, actionInfo);
  }
}

/**
 * Check validation errors that do not pass live schema checks.
 * todo: Graphic specific naming?
 */
export function checkErrorsAndTrack({
  possibleErrors,
  data,
  methodName,
}: {
  possibleErrors: Array<string>;
  data: AnyObject;
  methodName: string;
}): boolean {
  if (possibleErrors.length > 0) {
    log.error('Found validation errors:', methodName, data, possibleErrors);

    trackLiveError(
      new LiveError('Found corrupted object.', {
        code: ELiveErrorCode.CORRUPTED_OBJECT,
        data: {
          object: data,
          possibleErrors,
        },
      }),
      {
        method: methodName,
        category: ELiveErrorCategory.GRAPHICS,
      },
    );
    return false;
  }

  return true;
}

/**
 * Wrap callback with error handler to track exceptions automatically.
 */
export async function withLiveErrorTracking<T>(
  callback: () => T,
  additionalInfo?: ILiveErrorAdditionalInfo | (() => ILiveErrorAdditionalInfo),
): Promise<T> {
  try {
    return await callback();
  } catch (error) {
    trackLiveError(error, typeof additionalInfo === 'function' ? additionalInfo() : additionalInfo);
    throw error;
  }
}

/**
 * Update Datadog globals that will be sent with all page actions and all page views.
 */
export function updateDatadogGlobals(globals: AnyObject): void {
  log.info('Updating Datadog globals:', globals);

  if (!environmentConfig.IS_IFRAME && window.DD_RUM) {
    window.DD_RUM.onReady(() => {
      for (const key in globals) {
        window.DD_RUM.setViewContextProperty(key, String(globals[key]));
      }
    });
  }
}
